import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Search, MoreHorizontal, Edit2, Trash2, Eye, Send, CheckCircle, Clock, XCircle, BarChart2, Users, Download, UserPlus, Globe, Building2, AlertCircle, MessageSquare, SlidersHorizontal, X, CheckCheck, Mail, MailOpen, Loader2, Crown, UserMinus } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import api, { feedbackApi } from '../lib/api';
import { toast } from 'sonner';

const Forms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { workspaces, currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  const [forms, setForms] = useState([]);
  const [formsToFill, setFormsToFill] = useState([]);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [myApprovals, setMyApprovals] = useState([]);
  const [feedbackReceived, setFeedbackReceived] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workspaceFilter, setWorkspaceFilter] = useState(searchParams.get('workspace') || 'all');
  const [activeTab, setActiveTab] = useState('manage');
  
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitingByEmail, setInvitingByEmail] = useState(false);
  
  const [coOwners, setCoOwners] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [coOwnerEmail, setCoOwnerEmail] = useState('');
  const [addingCoOwner, setAddingCoOwner] = useState(false);
  const [savingCoOwners, setSavingCoOwners] = useState(false);
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isGlobalForm, setIsGlobalForm] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  useEffect(() => {
    fetchForms();
    fetchFormsToFill();
    fetchMySubmissions();
    fetchMyApprovals();
    fetchFeedbackReceived();
    fetchPendingApprovals();
  }, [workspaceFilter, statusFilter]);

  const fetchForms = async () => {
    try {
      let url = '/forms';
      const params = [];
      if (workspaceFilter && workspaceFilter !== 'all') params.push('workspace_id=' + workspaceFilter);
      if (statusFilter && statusFilter !== 'all') params.push('status=' + statusFilter);
      if (params.length) url += '?' + params.join('&');
      const response = await api.get(url);
      setForms(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFormsToFill = async () => {
    try {
      const response = await api.get('/forms-to-fill');
      setFormsToFill(response.data.forms || []);
    } catch (error) {
      console.error('Failed to fetch forms to fill:', error);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await api.get('/pending-approvals');
      setPendingApprovals(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const response = await api.get('/my-submissions');
      setMySubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to fetch my submissions:', error);
    }
  };

  const fetchMyApprovals = async () => {
    try {
      const response = await api.get('/my-approvals');
      setMyApprovals(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to fetch my approvals:', error);
    }
  };

  const fetchFeedbackReceived = async () => {
    try {
      const response = await api.get('/my-feedback/received');
      setFeedbackReceived(response.data.submissions || []);
    } catch (error) {
      console.error('Failed to fetch feedback received:', error);
    }
  };

  const fetchUsersAndGroups = async () => {
    try {
      const usersRes = await api.get('/admin/users');
      const groupsRes = await api.get('/admin/groups');
      setAllUsers(usersRes.data.users || []);
      setAllGroups(groupsRes.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch users/groups:', error);
    }
  };

  const handleCreateForm = () => {
    setSelectedWorkspace(workspaceFilter !== 'all' ? workspaceFilter : (currentWorkspace ? currentWorkspace.workspace_id : ''));
    setIsGlobalForm(false);
    setCreateDialogOpen(true);
  };

  const proceedToBuilder = () => {
    if (isGlobalForm) {
      navigate('/forms/builder?global=true');
    } else if (selectedWorkspace) {
      navigate('/forms/builder?workspace=' + selectedWorkspace);
    } else {
      toast.error('Please select a workspace or create a global form');
      return;
    }
    setCreateDialogOpen(false);
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form? All submissions will be lost.')) return;
    try {
      await api.delete('/forms/' + formId);
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
    }
  };

  const handlePublishForm = async (formId) => {
    try {
      await api.post('/forms/' + formId + '/publish');
      toast.success('Form published!');
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish form');
    }
  };

  const handleCloseForm = async (formId) => {
    try {
      await api.post('/forms/' + formId + '/close');
      toast.success('Form closed');
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close form');
    }
  };

  const openInviteDialog = async (form) => {
    setSelectedForm(form);
    setSelectedUsers(form.invited_users || []);
    setSelectedGroups(form.invited_groups || []);
    setInviteEmail('');
    setCoOwnerEmail('');
    setCoOwners(form.co_owner_details || []);
    if (isAdmin) {
      await fetchUsersAndGroups();
    }
    try {
      const res = await api.get('/users/assignable');
      setAssignableUsers(res.data.users || []);
    } catch (e) {
      console.error('Failed to fetch assignable users:', e);
    }
    setInviteDialogOpen(true);
  };

  const handleAddCoOwner = async () => {
    if (!selectedForm || !coOwnerEmail.trim()) return;
    
    // Support semicolon-separated emails
    const emails = coOwnerEmail.split(';').map(function(e) { return e.trim().toLowerCase(); }).filter(function(e) { return e; });
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    const invalidEmails = emails.filter(function(e) { return !emailRegex.test(e); });
    if (invalidEmails.length > 0) {
      toast.error('Invalid email(s): ' + invalidEmails.join(', '));
      return;
    }
    
    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    
    setAddingCoOwner(true);
    let successCount = 0;
    let failCount = 0;
    let updatedCoOwners = [...coOwners];
    const allKnown = [...assignableUsers, ...allUsers];
    
    for (const email of emails) {
      // Check if already a co-owner
      if (updatedCoOwners.some(function(c) { return c.email === email; })) {
        failCount++;
        continue;
      }
      
      let foundUser = allKnown.find(function(u) { return u.email === email; });
      
      if (!foundUser) {
        toast.error('No user found: ' + email);
        failCount++;
        continue;
      }
      
      if (foundUser.user_id === selectedForm.created_by) {
        toast.error('Form owner cannot be added as co-owner');
        failCount++;
        continue;
      }
      
      updatedCoOwners = [...updatedCoOwners, foundUser];
      successCount++;
    }
    
    if (successCount > 0) {
      try {
        setCoOwners(updatedCoOwners);
        await api.patch('/forms/' + selectedForm.form_id, {
          co_owners: updatedCoOwners.map(function(c) { return c.user_id; })
        });
        toast.success('Added ' + successCount + ' co-owner' + (successCount > 1 ? 's' : ''));
        setCoOwnerEmail('');
        fetchForms();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to add co-owner(s)');
      }
    } else if (failCount > 0) {
      toast.error('No new co-owners added');
    }
    
    setAddingCoOwner(false);
  };

  const handleRemoveCoOwner = async (userId) => {
    if (!selectedForm) return;
    const updatedCoOwners = coOwners.filter(function(c) { return c.user_id !== userId; });
    setCoOwners(updatedCoOwners);
    
    try {
      await api.patch('/forms/' + selectedForm.form_id, {
        co_owners: updatedCoOwners.map(function(c) { return c.user_id; })
      });
      toast.success('Co-owner removed');
      fetchForms();
    } catch (error) {
      toast.error('Failed to remove co-owner');
      // Revert on failure
      setCoOwners(coOwners);
    }
  };

  const handleRemoveInvitedUser = async (userId) => {
    if (!selectedForm) return;
    try {
      await api.post('/forms/' + selectedForm.form_id + '/invite', { user_ids: [userId], group_ids: [], action: 'remove' });
      toast.success('User removed');
      // Update local state to reflect removal
      setSelectedForm(function(prev) {
        if (!prev) return prev;
        return {
          ...prev,
          invited_users: (prev.invited_users || []).filter(function(id) { return id !== userId; }),
          invited_users_details: (prev.invited_users_details || []).filter(function(u) { return u.user_id !== userId; })
        };
      });
      setSelectedUsers(function(prev) { return prev.filter(function(id) { return id !== userId; }); });
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleInviteByEmail = async () => {
    if (!selectedForm || !inviteEmail.trim()) return;
    
    // Split by semicolons and trim each email
    const emails = inviteEmail.split(';').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    // Validate all emails
    const invalidEmails = emails.filter(e => !emailRegex.test(e));
    if (invalidEmails.length > 0) {
      toast.error('Invalid email(s): ' + invalidEmails.join(', '));
      return;
    }
    
    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    
    setInvitingByEmail(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const email of emails) {
      try {
        await api.post('/forms/' + selectedForm.form_id + '/invite-by-email', { email });
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    if (successCount > 0) toast.success(`Invited ${successCount} user${successCount > 1 ? 's' : ''} successfully`);
    if (failCount > 0) toast.error(`Failed to invite ${failCount} email${failCount > 1 ? 's' : ''}`);
    
    setInviteEmail('');
    fetchForms();
    setInvitingByEmail(false);
  };

  const handleSaveInvites = async () => {
    if (!selectedForm) return;
    try {
      const currentUsers = selectedForm.invited_users || [];
      const currentGroups = selectedForm.invited_groups || [];
      const usersToRemove = currentUsers.filter(function(u) { return !selectedUsers.includes(u); });
      const groupsToRemove = currentGroups.filter(function(g) { return !selectedGroups.includes(g); });
      if (usersToRemove.length > 0 || groupsToRemove.length > 0) {
        await api.post('/forms/' + selectedForm.form_id + '/invite', { user_ids: usersToRemove, group_ids: groupsToRemove, action: 'remove' });
      }
      const usersToAdd = selectedUsers.filter(function(u) { return !currentUsers.includes(u); });
      const groupsToAdd = selectedGroups.filter(function(g) { return !currentGroups.includes(g); });
      if (usersToAdd.length > 0 || groupsToAdd.length > 0) {
        await api.post('/forms/' + selectedForm.form_id + '/invite', { user_ids: usersToAdd, group_ids: groupsToAdd, action: 'add' });
      }
      toast.success('Invitations updated');
      setInviteDialogOpen(false);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invitations');
    }
  };

  const handleExport = async (formId, formName) => {
    try {
      const response = await api.get('/forms/' + formId + '/export');
      const data = response.data;
      const csvRows = [data.columns.join(',')];
      data.rows.forEach(function(row) {
        const rowData = data.columns.map(function(col) {
          const val = row[col] || '';
          if (typeof val === 'string' && (val.indexOf(',') >= 0 || val.indexOf('"') >= 0)) {
            return '"' + val.replace(/"/g, '""') + '"';
          }
          return val;
        });
        csvRows.push(rowData.join(','));
      });
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = formName.replace(/\s+/g, '_') + '_submissions.csv';
      link.click();
      toast.success('Export downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export');
    }
  };

  // Mark feedback as read
  const handleMarkFeedbackAsRead = async (submissionId, e) => {
    if (e) e.stopPropagation();
    try {
      await feedbackApi.markAsRead(submissionId);
      // Update local state
      setFeedbackReceived(prev => prev.map(f => 
        f.submission_id === submissionId ? { ...f, read_by_receiver: true } : f
      ));
      toast.success('Marked as read');
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  // Mark all feedback as read
  const handleMarkAllFeedbackAsRead = async () => {
    try {
      await feedbackApi.markAllAsRead();
      // Update local state
      setFeedbackReceived(prev => prev.map(f => ({ ...f, read_by_receiver: true })));
      toast.success('All feedback marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const filteredForms = forms.filter(function(form) {
    const q = searchQuery.toLowerCase();
    return form.name.toLowerCase().includes(q) || (form.description && form.description.toLowerCase().includes(q));
  });

  // Count for tabs
  const formsToFillCount = formsToFill.filter(function(f) { return !f.has_submitted; }).length;
  const unreadFeedbackCount = feedbackReceived.filter(function(f) { return !f.read_by_receiver; }).length;
  const activeFilterCount = (workspaceFilter !== 'all' ? 1 : 0) + (statusFilter !== 'all' ? 1 : 0);

  // Tab options for mobile dropdown
  const tabOptions = [
    { value: 'manage', label: 'Manage Forms', count: forms.length },
    { value: 'fill', label: 'Forms to Fill', count: formsToFillCount, highlight: true },
    { value: 'approvals', label: 'Pending Approvals', count: pendingApprovals.length, urgent: true },
    { value: 'feedbackreceived', label: 'Feedback Received', count: unreadFeedbackCount, highlight: unreadFeedbackCount > 0 },
    { value: 'myapprovals', label: 'My Approvals', count: myApprovals.length },
    { value: 'mysubmissions', label: 'My Submissions', count: mySubmissions.length }
  ];

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-600 text-[10px] px-1.5"><CheckCircle className="w-3 h-3 mr-0.5" />Active</Badge>;
    if (status === 'closed') return <Badge variant="destructive" className="text-[10px] px-1.5"><XCircle className="w-3 h-3 mr-0.5" />Closed</Badge>;
    return <Badge variant="secondary" className="text-[10px] px-1.5"><Edit2 className="w-3 h-3 mr-0.5" />Draft</Badge>;
  };

  const getSubmissionStatusBadge = (status) => {
    if (status === 'approved') return <Badge className="bg-green-600">Approved</Badge>;
    if (status === 'rejected') return <Badge variant="destructive">Rejected</Badge>;
    if (status === 'sent_back') return <Badge variant="outline" className="border-amber-500 text-amber-600">Sent Back</Badge>;
    return <Badge variant="secondary">Pending</Badge>;
  };

  return (
    <AppLayout>
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Mobile Header */}
      <div className="md:hidden mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-lg font-bold text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#4A9CC7]" />
              Forms
            </h1>
          </div>
          <Button onClick={handleCreateForm} size="sm" className="bg-[#4A9CC7] hover:bg-[#3d8ab5] h-9" data-testid="create-form-btn-mobile">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Mobile Tab Selector */}
        <Select value={activeTab} onValueChange={setActiveTab}>
          <SelectTrigger className="w-full mb-3" data-testid="mobile-tab-select">
            <SelectValue>
              {tabOptions.find(t => t.value === activeTab)?.label}
              {tabOptions.find(t => t.value === activeTab)?.count > 0 && (
                <Badge className="ml-2 text-[10px]" variant={tabOptions.find(t => t.value === activeTab)?.urgent ? 'destructive' : 'secondary'}>
                  {tabOptions.find(t => t.value === activeTab)?.count}
                </Badge>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {tabOptions.map(tab => (
              <SelectItem key={tab.value} value={tab.value}>
                <div className="flex items-center justify-between w-full">
                  <span>{tab.label}</span>
                  {tab.count > 0 && (
                    <Badge className="ml-2 text-[10px]" variant={tab.urgent ? 'destructive' : tab.highlight ? 'default' : 'secondary'}>
                      {tab.count}
                    </Badge>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Mobile Search and Filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search..." value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} className="pl-9 h-9" />
          </div>
          <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 relative">
                <SlidersHorizontal className="w-4 h-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#4A9CC7] text-white text-[10px] rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-auto rounded-t-2xl">
              <SheetHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <SheetTitle>Filters</SheetTitle>
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => { setWorkspaceFilter('all'); setStatusFilter('all'); }} className="text-[#4A9CC7]">
                      Clear all
                    </Button>
                  )}
                </div>
              </SheetHeader>
              <div className="space-y-4 pb-6">
                <div className="space-y-2">
                  <Label>Workspace</Label>
                  <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="All Workspaces" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Workspaces</SelectItem>
                      {workspaces.map(function(ws) { return <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full"><SelectValue placeholder="All Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full bg-[#4A9CC7] hover:bg-[#3d8ab5]" onClick={() => setMobileFiltersOpen(false)}>
                  Apply Filters
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Mobile Active Filters */}
        {activeFilterCount > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {workspaceFilter !== 'all' && (
              <Badge variant="secondary" className="text-xs py-0.5 px-2 flex items-center gap-1">
                {workspaces.find(w => w.workspace_id === workspaceFilter)?.name || 'Workspace'}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setWorkspaceFilter('all')} />
              </Badge>
            )}
            {statusFilter !== 'all' && (
              <Badge variant="secondary" className="text-xs py-0.5 px-2 flex items-center gap-1">
                {statusFilter}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setStatusFilter('all')} />
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Desktop Header */}
      <div className="hidden md:flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <FileText className="w-7 h-7 text-[#4A9CC7]" />
            Forms
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Create forms, collect responses, and review submissions</p>
        </div>
        <Button onClick={handleCreateForm} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]" data-testid="create-form-btn">
          <Plus className="w-4 h-4 mr-2" />Create Form
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        {/* Desktop Tabs */}
        <TabsList className="hidden md:flex flex-wrap">
          <TabsTrigger value="manage" data-testid="tab-manage">Manage Forms {forms.length > 0 && <Badge variant="secondary" className="ml-2">{forms.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="fill" data-testid="tab-fill">Forms to Fill {formsToFillCount > 0 && <Badge variant="default" className="ml-2 bg-[#4A9CC7]">{formsToFillCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals">Pending Approvals {pendingApprovals.length > 0 && <Badge variant="destructive" className="ml-2">{pendingApprovals.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="feedbackreceived" data-testid="tab-feedbackreceived">Feedback Received {unreadFeedbackCount > 0 && <Badge className="ml-2 bg-purple-600">{unreadFeedbackCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="myapprovals" data-testid="tab-myapprovals">My Approvals {myApprovals.length > 0 && <Badge className="ml-2 bg-green-600">{myApprovals.length}</Badge>}</TabsTrigger>
          <TabsTrigger value="mysubmissions" data-testid="tab-mysubmissions">My Submissions {mySubmissions.length > 0 && <Badge variant="secondary" className="ml-2">{mySubmissions.length}</Badge>}</TabsTrigger>
        </TabsList>

        <TabsContent value="manage" className="space-y-4">
          {/* Desktop Filters */}
          <div className="hidden md:flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input placeholder="Search forms..." value={searchQuery} onChange={function(e) { setSearchQuery(e.target.value); }} className="pl-9" data-testid="search-forms" />
            </div>
            <Select value={workspaceFilter} onValueChange={setWorkspaceFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="workspace-filter"><SelectValue placeholder="All Workspaces" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Workspaces</SelectItem>
                {workspaces.map(function(ws) { return <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>; })}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading forms...</div>
          ) : filteredForms.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-8 md:py-12 text-center">
                <FileText className="w-10 h-10 md:w-12 md:h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-base md:text-lg font-medium mb-2">No forms yet</h3>
                <p className="text-muted-foreground text-sm mb-4">Create your first form to start collecting responses</p>
                <Button onClick={handleCreateForm} variant="outline" size="sm"><Plus className="w-4 h-4 mr-2" />Create Form</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {filteredForms.map(function(form) {
                return (
                  <Card key={form.form_id} className="hover:shadow-md transition-shadow" data-testid={'form-card-' + form.form_id}>
                    <CardHeader className="pb-2 md:pb-3 p-3 md:p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardTitle className="text-sm md:text-base truncate">{form.name}</CardTitle>
                            {form.is_global && <Globe className="w-3 h-3 md:w-4 md:h-4 text-[#4A9CC7] flex-shrink-0" title="Global Form" />}
                          </div>
                          <CardDescription className="text-[10px] md:text-xs mt-1 truncate flex items-center gap-1">
                            {form.is_global ? <><Globe className="w-3 h-3" /> Global</> : <><Building2 className="w-3 h-3" /> {form.workspace_name}</>}
                          </CardDescription>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-7 w-7 md:h-8 md:w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {form.is_owner && (
                              <DropdownMenuItem onClick={function() { navigate('/forms/builder/' + form.form_id); }}><Edit2 className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                            )}
                            {form.is_owner && (
                              <DropdownMenuItem onClick={function() { navigate('/forms/' + form.form_id + '/submissions'); }}><BarChart2 className="w-4 h-4 mr-2" />View Submissions</DropdownMenuItem>
                            )}
                            {form.is_owner && (
                              <>
                                <DropdownMenuItem onClick={function() { navigate('/forms/' + form.form_id + '/dashboard'); }}><Eye className="w-4 h-4 mr-2" />Owner Dashboard</DropdownMenuItem>
                                <DropdownMenuItem onClick={function() { openInviteDialog(form); }}><UserPlus className="w-4 h-4 mr-2" />Manage Access</DropdownMenuItem>
                                <DropdownMenuItem onClick={function() { handleExport(form.form_id, form.name); }}><Download className="w-4 h-4 mr-2" />Export to CSV</DropdownMenuItem>
                              </>
                            )}
                            {form.is_owner && form.status === 'draft' && <DropdownMenuItem onClick={function() { handlePublishForm(form.form_id); }}><Send className="w-4 h-4 mr-2" />Publish</DropdownMenuItem>}
                            {form.is_owner && form.status === 'active' && <DropdownMenuItem onClick={function() { handleCloseForm(form.form_id); }}><XCircle className="w-4 h-4 mr-2" />Close Form</DropdownMenuItem>}
                            {!form.is_owner && form.status === 'active' && (
                              <DropdownMenuItem onClick={function() { navigate('/forms/' + form.form_id + '/fill'); }}><Send className="w-4 h-4 mr-2" />Fill Form</DropdownMenuItem>
                            )}
                            {form.is_owner && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={function() { handleDeleteForm(form.form_id); }} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {form.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{form.description}</p>}
                      <div className="flex items-center justify-between">
                        {getStatusBadge(form.status)}
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{form.fields ? form.fields.length : 0} fields</span>
                          <span>-</span>
                          <span>{form.submission_count || 0} responses</span>
                        </div>
                      </div>
                      {form.is_owner && ((form.invited_users && form.invited_users.length > 0) || (form.invited_groups && form.invited_groups.length > 0)) && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                          <Users className="w-3 h-3" />
                          {form.invited_users ? form.invited_users.length : 0} users, {form.invited_groups ? form.invited_groups.length : 0} groups invited
                        </div>
                      )}
                      {form.co_owner_details && form.co_owner_details.length > 0 && (
                        <div className="mt-1 flex items-center gap-1 text-xs text-amber-600" data-testid={'form-co-owners-badge-' + form.form_id}>
                          <Crown className="w-3 h-3" />
                          {form.co_owner_details.length} co-owner{form.co_owner_details.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="fill" className="space-y-4">
          {formsToFill.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-medium mb-2">All caught up!</h3>
                <p className="text-muted-foreground">No forms pending your response</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {formsToFill.map(function(form) {
                var canClick = !form.has_submitted || form.submission_status === 'sent_back';
                return (
                  <Card key={form.form_id} className={'hover:shadow-md transition-shadow cursor-pointer ' + (!canClick ? 'opacity-60' : '')} onClick={function() { if (canClick) navigate('/forms/' + form.form_id + '/fill'); }} data-testid={'fill-form-' + form.form_id}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">{form.name}</CardTitle>
                      <CardDescription className="text-xs">{form.workspace_name}</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {form.description && <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{form.description}</p>}
                      <div className="flex items-center justify-between">
                        {form.has_submitted ? getSubmissionStatusBadge(form.submission_status) : <Badge variant="outline" className="text-[#4A9CC7] border-[#4A9CC7]"><Clock className="w-3 h-3 mr-1" />Pending</Badge>}
                        <span className="text-sm text-muted-foreground">{form.fields ? form.fields.length : 0} questions</span>
                      </div>
                      {form.submission_status === 'sent_back' && (
                        <p className="mt-2 text-xs text-amber-600 font-medium">Click to resubmit with changes</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approvals" className="space-y-4">
          {pendingApprovals.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                <h3 className="text-lg font-medium mb-2">No pending approvals</h3>
                <p className="text-muted-foreground">All submissions have been reviewed</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {pendingApprovals.map(function(sub) {
                return (
                  <Card key={sub.submission_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={function() { navigate('/forms/' + sub.form_id + '/submissions'); }} data-testid={'approval-' + sub.submission_id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center"><AlertCircle className="w-5 h-5 text-amber-600" /></div>
                          <div>
                            <h4 className="font-medium">{sub.form_name}</h4>
                            <p className="text-sm text-muted-foreground">Submitted by {sub.submitted_by_name} - {new Date(sub.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {getSubmissionStatusBadge(sub.status)}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="feedbackreceived" className="space-y-4">
          {feedbackReceived.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No feedback received yet</h3>
                <p className="text-muted-foreground">Feedback from others about you will appear here once approved</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Header with Mark All as Read */}
              {unreadFeedbackCount > 0 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {unreadFeedbackCount} unread feedback{unreadFeedbackCount !== 1 ? 's' : ''}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkAllFeedbackAsRead}
                    className="text-xs"
                    data-testid="mark-all-feedback-read-btn"
                  >
                    <CheckCheck className="w-4 h-4 mr-1" />
                    Mark All as Read
                  </Button>
                </div>
              )}
              
              <div className="space-y-3">
                {feedbackReceived.map(function(sub) {
                  const isUnread = !sub.read_by_receiver;
                  return (
                    <Card 
                      key={sub.submission_id} 
                      className={`hover:shadow-md transition-shadow cursor-pointer ${
                        isUnread 
                          ? 'border-purple-400 dark:border-purple-600 bg-purple-50/50 dark:bg-purple-900/20' 
                          : 'border-purple-200 dark:border-purple-800'
                      }`}
                      onClick={function() { 
                        if (isUnread) handleMarkFeedbackAsRead(sub.submission_id);
                        navigate('/forms/' + sub.form_id + '/view/' + sub.submission_id); 
                      }} 
                      data-testid={'feedback-' + sub.submission_id}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isUnread 
                                ? 'bg-purple-200 dark:bg-purple-800' 
                                : 'bg-purple-100 dark:bg-purple-900'
                            }`}>
                              {isUnread ? (
                                <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              ) : (
                                <MailOpen className="w-5 h-5 text-purple-500 dark:text-purple-500" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className={`font-medium truncate ${isUnread ? 'font-semibold' : ''}`}>{sub.form_name}</h4>
                                {isUnread && (
                                  <span className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0" />
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground truncate">
                                From {sub.submitted_by_name} - {new Date(sub.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {isUnread && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleMarkFeedbackAsRead(sub.submission_id, e)}
                                className="h-8 px-2 text-xs hidden sm:flex"
                                title="Mark as read"
                                data-testid={'mark-read-' + sub.submission_id}
                              >
                                <CheckCheck className="w-4 h-4" />
                              </Button>
                            )}
                            <Badge className={isUnread ? 'bg-purple-600' : 'bg-purple-400'}>
                              {isUnread ? 'New' : 'Read'}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="myapprovals" className="space-y-4">
          {myApprovals.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No approvals yet</h3>
                <p className="text-muted-foreground">Submissions you have reviewed will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {myApprovals.map(function(sub) {
                return (
                  <Card key={sub.submission_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={function() { navigate('/forms/' + sub.form_id + '/submissions/' + sub.submission_id); }} data-testid={'myapproval-' + sub.submission_id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={'w-10 h-10 rounded-full flex items-center justify-center ' + (sub.status === 'approved' ? 'bg-green-100' : sub.status === 'rejected' ? 'bg-red-100' : 'bg-amber-100')}>
                            {sub.status === 'approved' ? <CheckCircle className="w-5 h-5 text-green-600" /> : sub.status === 'rejected' ? <XCircle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-amber-600" />}
                          </div>
                          <div>
                            <h4 className="font-medium">{sub.form_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              Submitted by {sub.submitted_by_name} - {new Date(sub.updated_at || sub.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        {getSubmissionStatusBadge(sub.status)}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mysubmissions" className="space-y-4">
          {mySubmissions.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No submissions yet</h3>
                <p className="text-muted-foreground">Your form submissions will appear here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {mySubmissions.map(function(sub) {
                return (
                  <Card key={sub.submission_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={function() { navigate('/forms/' + sub.form_id + '/view/' + sub.submission_id); }} data-testid={'mysub-' + sub.submission_id}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#4A9CC7]/10 flex items-center justify-center"><FileText className="w-5 h-5 text-[#4A9CC7]" /></div>
                          <div>
                            <h4 className="font-medium">{sub.form_name}</h4>
                            <p className="text-sm text-muted-foreground">Submitted {new Date(sub.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {getSubmissionStatusBadge(sub.status)}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Form</DialogTitle>
            <DialogDescription>Choose whether to create a global form or link it to a workspace</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox id="global-form" checked={isGlobalForm} onCheckedChange={setIsGlobalForm} />
              <Label htmlFor="global-form" className="flex items-center gap-2"><Globe className="w-4 h-4" />Create as Global Form (independent of workspace)</Label>
            </div>
            {!isGlobalForm && (
              <div className="space-y-2">
                <Label>Link to Workspace</Label>
                <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                  <SelectTrigger><SelectValue placeholder="Select a workspace" /></SelectTrigger>
                  <SelectContent>
                    {workspaces.map(function(ws) { return <SelectItem key={ws.workspace_id} value={ws.workspace_id}>{ws.name}</SelectItem>; })}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setCreateDialogOpen(false); }}>Cancel</Button>
            <Button onClick={proceedToBuilder} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]">Continue to Builder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Form Access</DialogTitle>
            <DialogDescription>Invite users who can fill this form</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Co-owners Section - Only visible to form creator */}
            {selectedForm?.created_by === user?.user_id && (
              <div data-testid="co-owners-section">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  <Label className="text-sm font-medium">Co-owners</Label>
                </div>
                <p className="text-xs text-muted-foreground mb-2">Co-owners can edit the form, manage access, view submissions, and export data.</p>
                
                {/* Current co-owners */}
                {coOwners.length > 0 && (
                  <div className="border rounded-lg max-h-32 overflow-y-auto p-2 space-y-1 bg-amber-50/50 dark:bg-amber-950/20 mb-2" data-testid="co-owners-list">
                    {coOwners.map(function(co) {
                      return (
                        <div key={co.user_id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-muted group" data-testid={'co-owner-' + co.user_id}>
                          <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center text-[10px] font-medium text-amber-600 flex-shrink-0">
                            {(co.name || co.email || '?')[0].toUpperCase()}
                          </div>
                          <span className="truncate font-medium">{co.name || co.email}</span>
                          <span className="text-xs text-muted-foreground truncate ml-auto mr-2">{co.email}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={function() { handleRemoveCoOwner(co.user_id); }}
                            data-testid={'remove-co-owner-' + co.user_id}
                          >
                            <UserMinus className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Add co-owner by email */}
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="email1@example.com; email2@example.com"
                    value={coOwnerEmail}
                    onChange={function(e) { setCoOwnerEmail(e.target.value); }}
                    onKeyDown={function(e) { if (e.key === 'Enter') handleAddCoOwner(); }}
                    data-testid="co-owner-email-input"
                    className="text-sm"
                  />
                  <Button
                    onClick={handleAddCoOwner}
                    disabled={addingCoOwner || !coOwnerEmail.trim()}
                    variant="outline"
                    className="border-amber-300 hover:bg-amber-50 text-amber-700"
                    data-testid="add-co-owner-btn"
                  >
                    {addingCoOwner ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Separate multiple emails with semicolons (;)</p>
                
                {/* Quick add from assignable users dropdown - Admin only */}
                {isAdmin && assignableUsers.length > 0 && (
                  <Select onValueChange={function(val) { setCoOwnerEmail(val); }}>
                    <SelectTrigger className="mt-2 text-sm" data-testid="co-owner-user-select">
                      <SelectValue placeholder="Or select from your workspaces..." />
                    </SelectTrigger>
                    <SelectContent>
                      {assignableUsers
                        .filter(function(u) {
                          return u.user_id !== selectedForm?.created_by 
                            && !coOwners.some(function(c) { return c.user_id === u.user_id; });
                        })
                        .map(function(u) {
                          return (
                            <SelectItem key={u.user_id} value={u.email}>
                              {u.name} ({u.email})
                            </SelectItem>
                          );
                        })}
                    </SelectContent>
                  </Select>
                )}
                
                <div className="relative my-3">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Invite Users</span>
                  </div>
                </div>
              </div>
            )}

            {/* Current Access List */}
            {selectedForm?.invited_users_details?.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Users with Access ({selectedForm.invited_users_details.length})
                </Label>
                <div className="border rounded-lg max-h-32 overflow-y-auto p-2 space-y-1 bg-muted/20">
                  {selectedForm.invited_users_details.map(function(u) {
                    return (
                      <div key={u.user_id} className="flex items-center gap-2 text-sm py-1 px-2 rounded hover:bg-muted group" data-testid={`access-user-${u.user_id}`}>
                        <div className="w-6 h-6 rounded-full bg-[#4A9CC7]/20 flex items-center justify-center text-[10px] font-medium text-[#4A9CC7] flex-shrink-0">
                          {(u.name || u.email || '?')[0].toUpperCase()}
                        </div>
                        <span className="truncate">{u.name || u.email}</span>
                        <span className="text-xs text-muted-foreground truncate ml-auto mr-2">{u.email}</span>
                        {selectedForm.is_owner && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                            onClick={function() { handleRemoveInvitedUser(u.user_id); }}
                            data-testid={'remove-invited-user-' + u.user_id}
                          >
                            <UserMinus className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Workspace access note */}
            {selectedForm?.workspace_id && (
              <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/30 p-2 rounded">
                All members of workspace "{selectedForm.workspace_name}" also have access to this form.
              </p>
            )}

            {/* Email invite - available to all users */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Invite by Email</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="email1@example.com; email2@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleInviteByEmail()}
                  data-testid="invite-email-input"
                />
                <Button 
                  onClick={handleInviteByEmail} 
                  disabled={invitingByEmail || !inviteEmail.trim()}
                  className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                  data-testid="invite-email-btn"
                >
                  {invitingByEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Separate multiple emails with semicolons (;)</p>
            </div>

            {/* Multi-select users and groups - Admin only */}
            {isAdmin && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Admin Options</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Select Multiple Users</Label>
                  <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-2">
                    {allUsers.length === 0 ? <p className="text-sm text-muted-foreground p-2">No users available</p> : allUsers.map(function(u) {
                      return (
                        <div key={u.user_id} className="flex items-center space-x-2">
                          <Checkbox id={'user-' + u.user_id} checked={selectedUsers.includes(u.user_id)} onCheckedChange={function(checked) { if (checked) { setSelectedUsers([...selectedUsers, u.user_id]); } else { setSelectedUsers(selectedUsers.filter(function(id) { return id !== u.user_id; })); } }} />
                          <Label htmlFor={'user-' + u.user_id} className="font-normal text-sm">{u.name} <span className="text-muted-foreground">({u.email})</span></Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-2 block">User Groups</Label>
                  <div className="border rounded-lg max-h-40 overflow-y-auto p-2 space-y-2">
                    {allGroups.length === 0 ? <p className="text-sm text-muted-foreground p-2">No groups available</p> : allGroups.map(function(group) {
                      return (
                        <div key={group.group_id} className="flex items-center space-x-2">
                          <Checkbox id={'group-' + group.group_id} checked={selectedGroups.includes(group.group_id)} onCheckedChange={function(checked) { if (checked) { setSelectedGroups([...selectedGroups, group.group_id]); } else { setSelectedGroups(selectedGroups.filter(function(id) { return id !== group.group_id; })); } }} />
                          <Label htmlFor={'group-' + group.group_id} className="font-normal text-sm">{group.name} <span className="text-muted-foreground">({group.member_count} members)</span></Label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setInviteDialogOpen(false); }} data-testid="invite-dialog-cancel">Close</Button>
            {isAdmin && <Button onClick={handleSaveInvites} className="bg-[#4A9CC7] hover:bg-[#3d8ab5]" data-testid="save-access-btn">Save Access</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AppLayout>
  );
};

export default Forms;
